import React from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { Icon } from '@common/components/icon';
import { combineClassNames } from '@common/utils/combineClassNames';

import './message.scss';

export type MessageProps = Record<string, string | number> | {
  warning?: string | boolean;
  error?: string | boolean;
};

export type ErrorType = {
  translationKey: string;
  props: Record<string, string | number>;
};

type MessageOwnProps = MessageProps & {
  id?: string;
};

type MessagesProps = {
  errors: ErrorType[];
  /** Renders a dismiss button. Pass it for errors nothing else clears, e.g. a rejected file. */
  onClose?: () => void;
};

export const Messages = ({ errors, onClose }: MessagesProps) => {
  const { t } = useTranslation();

  return (
    <div className={combineClassNames('Messages', { 'Messages--dismissible': !!onClose })}>
      <div className="Messages__List">
        {errors.map(({ translationKey, props }) => (
          // Keyed by content: the list is rebuilt per batch, never reordered.
          <div className="Messages__Item" key={`${translationKey}:${Object.values(props || {}).join('|')}`}>
            <Message error={translationKey} {...props} />
          </div>
        ))}
      </div>
      {onClose && (
        <button
          type="button"
          className="Messages__Close"
          aria-label={t('common:close')}
          onClick={onClose}
        >
          <Icon type="close" />
        </button>
      )}
    </div>
  );
};

export const Message = ({ error, warning, id, ...props }: MessageOwnProps) => {
  let message;
  let className = '';
  if (error && typeof error === 'string') {
    message = error;
    className = 'Form__control__error';
  }
  if (warning && typeof warning === 'string') {
    message = warning;
    className = 'Form__control__warning';
  }

  if (!message) return null;

  return (
    <small id={id} className={className} role={id ? 'alert' : undefined}>
      <Trans i18nKey={message} values={props}>{message}</Trans>
    </small>
  );
};
